import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isGestion } from '../lib/roles';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  AlertTriangle, ClipboardCheck, Plus, FileText, Trash2, Search, Edit2, Stethoscope, Scissors, Info, Receipt, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto } from '../lib/utils';
import { todayStr } from '../lib/citasApi';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { PatientCombobox } from '../components/PatientCombobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/*
 * JUSTIFICANTES (5/10/2026, administración)
 * -----------------------------------------
 *  - Tercer tipo, el de PAGO: se hace a partir de un cobro del paciente (o
 *    desde el botón «Justificante» de la fila del cobro, que trae aquí
 *    `?pago=<cobro>&paciente=<paciente>`). El importe y la fecha los pone el
 *    servidor desde el cobro; el texto es el pedido, letra por letra, y se
 *    puede retocar antes de imprimir. Es dinero: solo gestión lo ve, como los
 *    cobros (el servidor tampoco se lo lista a nadie más).
 *  - En los tres, el CENTRO con el que sale (logo, nombre, dirección y
 *    teléfono): las marcas propias de Configuración → Marcas, ninguna escrita
 *    aquí. Se propone el de la agenda de la cita (el servidor dice cuál y por
 *    qué) y se guarda con el justificante.
 *  - Sin emojis: iconos de lucide o texto.
 *
 * EL DE ACOMPAÑANTE (21/10/2026, administración)
 *  - De quien acompaña a un paciente. El paciente acompañado se elige del
 *    buscador (el justificante se guarda con él y sale en sus justificantes);
 *    el acompañante se escribe a mano (nombre, apellidos y DNI, NIE o
 *    pasaporte) y NO se le crea ficha. La fecha, la de la visita: hoy, y se
 *    puede cambiar. El texto lo pone el servidor, como el de los demás.
 *  - El listado no trae el documento del acompañante: «Editar» lo pide al
 *    servidor con el justificante entero.
 *
 * LA FECHA DEL PAGO (27/10/2026, administración)
 *  - En el de pago, debajo del cobro, un campo «Fecha del pago» de solo
 *    lectura con la fecha del cobro elegido (nadie la escribe: viene del
 *    registro de cobros) y el aviso de dónde sale.
 *  - Sin ningún cobro registrado no se puede generar: el mensaje lo dice y el
 *    botón se queda apagado hasta que haya un cobro elegido.
 *  - Si el paciente tiene otro cobro con el mismo importe (`mismo_importe`,
 *    lo dice el servidor), se pregunta cuál es con botones de radio antes de
 *    generar, y el servidor lo exige (`cobro_confirmado`).
 *  - Sin emojis ni iconos nuevos: texto.
 */

const AVISO_FECHA_DEL_PAGO = 'La fecha se obtiene automáticamente del registro de cobros. '
  + 'Si no aparece, es necesario registrar el cobro antes de generar el justificante.';

const TIPOS = {
  consulta: { etiqueta: 'Consulta', titulo: 'Justificante consulta', icono: Stethoscope },
  quirofano: { etiqueta: 'Quirófano', titulo: 'Justificante quirófano', icono: Scissors },
  pago: { etiqueta: 'Pago', titulo: 'Justificante de pago', icono: Receipt },
  acompanante: { etiqueta: 'Acompañante', titulo: 'Justificante de acompañante', icono: Users },
};

/** «Nombre Apellido1 Apellido2» de quien acompañó. */
function nombreAcompanante(j) {
  return [j?.acompanante_nombre, j?.acompanante_apellido1, j?.acompanante_apellido2]
    .map((p) => String(p || '').trim()).filter(Boolean).join(' ');
}

/** «a», «a y b», «a, b y c». */
function enFrase(partes) {
  return partes.length < 2 ? (partes[0] || '') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/** «05/10/2026» de «2026-10-05» (el formato del texto del justificante). */
function fechaCorta(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || '');
}

/** «1.234,50»: el importe como en el texto del justificante (miles con
 *  punto siempre; `toLocaleString('es-ES')` no los pone en 4 cifras). */
function importeES(valor) {
  const n = Number(valor) || 0;
  const [entero, decimales] = Math.abs(n).toFixed(2).split('.');
  return `${n < 0 ? '-' : ''}${entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimales}`;
}

/** Por qué se propone el formato que se propone (lo dice el servidor). El
 *  «centro» de siempre se enseña como «Formato» desde el 27/10/2026
 *  (administración: el selector de formato de todos los documentos); por
 *  dentro sigue siendo la marca propia (`marca_clave`). */
const PISTA_DEL_CENTRO = {
  // El del médico de la cita (26/10/2026): el mismo membrete que el resto de
  // sus documentos (el titular, el de Configuración). Se propone y se cambia.
  medico: 'Propuesto por el médico de la cita; puedes cambiarlo antes de generar.',
  agenda: 'Propuesto: el de la agenda de la cita de ese día.',
  configuracion: 'Propuesto: la agenda de la cita no tiene centro propio, así que el de Configuración.',
  sin_cita: 'No se sabe por la cita: se propone el de Configuración. Cámbialo si no es.',
  primero: 'No se sabe por la cita: se propone el primero. Cámbialo si no es.',
  cobro: 'Propuesto: el del cobro (su agenda o la cita de ese día).',
};

const vacio = (type) => ({
  patient_id: '',
  type: type || 'consulta',
  // Hoy en la hora de aquí (con `toISOString` era el día de Greenwich: de
  // madrugada salía la fecha de ayer).
  event_date: todayStr(),
  time_from: '',
  time_to: '',
  procedure: '',
  rest_days: '',
  companion_name: '',
  additional_notes: '',
  custom_body: '',
  marca_clave: '',
  cobro_id: '',
  acompanante_nombre: '',
  acompanante_apellido1: '',
  acompanante_apellido2: '',
  acompanante_documento: '',
});

function JustificationDialog({
  open, onOpenChange, initial, patients, onSaved, defaultType, preset, puedePago,
}) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => vacio(defaultType));
  const [advanced, setAdvanced] = useState(false);
  const [centros, setCentros] = useState(null);       // null mientras llegan
  const [motivo, setMotivo] = useState('');
  // Si quien rellena ya ha elegido el centro, no se le cambia por detrás.
  const centroTocado = useRef(false);
  const [cobros, setCobros] = useState(null);         // null = sin pedir / llegando
  const [pacienteDelCobro, setPacienteDelCobro] = useState('');
  const [errorCobros, setErrorCobros] = useState('');
  // Con cobros del mismo importe (27/10/2026): si quien rellena ya ha marcado
  // cuál es. Se olvida al cambiar de paciente o de cobro desde el selector.
  const [cobroConfirmado, setCobroConfirmado] = useState(false);

  useEffect(() => {
    if (!open) return;
    centroTocado.current = isEdit;  // editando se queda el suyo
    if (initial) {
      setForm({ ...vacio(initial.type), ...initial, rest_days: initial.rest_days || '', marca_clave: initial.marca_clave || '' });
    } else {
      setForm({ ...vacio(defaultType), ...(preset || {}) });
    }
    setAdvanced(!!initial?.custom_body);
    setMotivo('');
    setPacienteDelCobro('');
    setCobroConfirmado(false);
  }, [open, initial, defaultType, preset, isEdit]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tipo = form.type;
  const isQX = tipo === 'quirofano';
  const isPago = tipo === 'pago';
  const isAcomp = tipo === 'acompanante';
  const pacienteId = form.patient_id;
  const fechaDelCentro = isPago ? '' : form.event_date;

  // Editando uno de acompañante: el listado no trae su documento, se pide el
  // justificante entero. 'listo' | 'cargando' | 'error'.
  const [docAcomp, setDocAcomp] = useState('listo');
  useEffect(() => {
    if (!open || !initial?.id || initial?.type !== 'acompanante') {
      setDocAcomp('listo');
      return undefined;
    }
    let vivo = true;
    setDocAcomp('cargando');
    api.get(`/api/justificantes/${initial.id}`)
      .then((r) => {
        if (!vivo) return;
        setForm((f) => ({ ...f, acompanante_documento: r?.data?.acompanante_documento || '' }));
        setDocAcomp('listo');
      })
      .catch(() => { if (vivo) setDocAcomp('error'); });
    return () => { vivo = false; };
  }, [open, initial]);

  // Los centros que se pueden elegir y el que se propone para este paciente
  // y esta fecha. En el de pago lo propone el cobro (más abajo).
  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    const params = { tipo };
    if (pacienteId) params.patient_id = pacienteId;
    if (fechaDelCentro) params.fecha = fechaDelCentro;
    api.get('/api/justificantes/centros', { params })
      .then((r) => {
        if (!vivo) return;
        setCentros(Array.isArray(r?.data?.centros) ? r.data.centros : []);
        if (!centroTocado.current && tipo !== 'pago') {
          setForm((f) => ({ ...f, marca_clave: r?.data?.por_defecto || '' }));
          setMotivo(r?.data?.motivo || '');
        }
      })
      .catch(() => { if (vivo) setCentros([]); });
    return () => { vivo = false; };
  }, [open, pacienteId, fechaDelCentro, tipo]);

  // Los cobros del paciente, para el de pago nuevo.
  useEffect(() => {
    if (!open || !isPago || isEdit || !pacienteId) {
      setCobros(null);
      return undefined;
    }
    let vivo = true;
    setCobros(null);
    setErrorCobros('');
    api.get('/api/justificantes/cobros', { params: { patient_id: pacienteId } })
      .then((r) => {
        if (!vivo) return;
        setCobros(Array.isArray(r?.data?.cobros) ? r.data.cobros : []);
        setPacienteDelCobro(r?.data?.paciente || '');
      })
      .catch((e) => {
        if (!vivo) return;
        setCobros([]);
        setErrorCobros(e?.response?.data?.detail || 'No se han podido cargar los cobros del paciente.');
      });
    return () => { vivo = false; };
  }, [open, isPago, isEdit, pacienteId]);

  const cobroElegido = useMemo(
    () => (cobros || []).find((c) => c.id === form.cobro_id) || null,
    [cobros, form.cobro_id],
  );

  // Elegido el cobro (o venido de la fila del cobro), su centro.
  useEffect(() => {
    if (cobroElegido && !centroTocado.current && cobroElegido.centro_sugerido) {
      setForm((f) => ({ ...f, marca_clave: cobroElegido.centro_sugerido }));
      setMotivo('cobro');
    }
  }, [cobroElegido]);

  const textoPago = isEdit ? (initial?.texto_pago || '') : (cobroElegido?.texto || '');
  const cobroQueNoEsta = isPago && !isEdit && form.cobro_id && Array.isArray(cobros) && !cobroElegido;
  // La fecha del pago (27/10/2026): la del cobro elegido; editando, la suya.
  // Vacía hasta que haya cobro.
  const fechaDelPago = isEdit ? fechaCorta(initial?.event_date) : (cobroElegido ? fechaCorta(cobroElegido.fecha) : '');
  // Los cobros con el mismo importe que el elegido, en el orden de la lista
  // (no «el elegido primero»: al marcar otro se reordenarían bajo el cursor).
  // Si hay, se pregunta cuál es antes de generar.
  const cobrosIguales = useMemo(() => {
    const otros = cobroElegido?.mismo_importe;
    if (!Array.isArray(otros) || otros.length === 0) return [];
    return (cobros || []).filter((c) => c.id === cobroElegido.id || otros.includes(c.id));
  }, [cobroElegido, cobros]);
  const hayQueConfirmar = isPago && !isEdit && cobrosIguales.length > 0;
  // En el de pago nuevo, sin cobro elegido no hay nada que generar.
  const sinCobro = isPago && !isEdit && !cobroElegido;

  const elegirCobro = (v) => {
    // El mismo eco de Radix que en el centro (abajo): solo cuenta un cambio.
    if (!v || v === form.cobro_id) return;
    setCobroConfirmado(false);
    setF('cobro_id', v);
  };

  const confirmarCobro = (id) => {
    setCobroConfirmado(true);
    setF('cobro_id', id);
  };
  // El paciente que no se puede cambiar: editando, o venido de un cobro.
  const pacienteFijo = isEdit
    ? (initial?.patient_name || '—')
    : (preset?.patient_id ? (pacienteDelCobro || (cobros === null ? 'Cargando…' : '—')) : '');

  // Radix, dentro de un <form>, repite `onValueChange` cada vez que el valor
  // cambia por programa (su <select> oculto lanza un «change»), y con «» si
  // aún no tiene esa opción. Ni el eco ni el vacío son una elección de nadie:
  // si se tomaran por una, la propuesta del cobro se borraría sola.
  const elegirCentro = (v) => {
    if (!v || v === form.marca_clave) return;
    centroTocado.current = true;
    setMotivo('');
    setF('marca_clave', v);
  };

  const cambiarTexto = (marcado) => {
    setAdvanced(marcado);
    if (!marcado) setF('custom_body', '');
    // En el de pago se edita el texto que ya está escrito, no uno en blanco.
    else if (isPago && !form.custom_body) setF('custom_body', textoPago);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error('Selecciona un paciente');
    if (isPago && !form.cobro_id) return toast.error('Elige el cobro del que sale el justificante');
    if (hayQueConfirmar && !cobroConfirmado) {
      return toast.error('Hay cobros con el mismo importe: marca cuál corresponde antes de generar');
    }
    if (!isPago && !form.event_date) return toast.error('Indica la fecha');
    // Sin el documento cargado (editando), se guarda sin tocarlo.
    const sinDocumento = isEdit && docAcomp === 'error' && !String(form.acompanante_documento || '').trim();
    if (isAcomp) {
      if (isEdit && docAcomp === 'cargando') return toast.error('Espera: se están cargando los datos del acompañante');
      const faltan = [];
      if (!String(form.acompanante_nombre || '').trim()) faltan.push('el nombre');
      if (!String(form.acompanante_apellido1 || '').trim()) faltan.push('el primer apellido');
      if (!String(form.acompanante_documento || '').trim() && !sinDocumento) faltan.push('el DNI, NIE o pasaporte');
      if (faltan.length) return toast.error(`Del acompañante falta ${enFrase(faltan)}`);
    }
    if (!isEdit && (centros || []).length > 0 && !form.marca_clave) return toast.error('Elige el centro');
    try {
      let payload;
      if (isPago) {
        // Del de pago, el servidor pone el importe y la fecha desde el cobro.
        payload = {
          patient_id: form.patient_id, type: 'pago', cobro_id: form.cobro_id,
          marca_clave: form.marca_clave, additional_notes: form.additional_notes || '',
          custom_body: advanced ? (form.custom_body || '') : '',
        };
        // Solo cuando se ha marcado cuál de los iguales es: el servidor lo exige.
        if (hayQueConfirmar && cobroConfirmado) payload.cobro_confirmado = true;
      } else if (isAcomp) {
        // Solo lo suyo: quién acompañó, la fecha de la visita y el centro. El
        // texto lo pone el servidor; el segundo apellido puede ir vacío.
        payload = {
          patient_id: form.patient_id, type: 'acompanante', event_date: form.event_date,
          marca_clave: form.marca_clave,
          acompanante_nombre: String(form.acompanante_nombre || '').trim(),
          acompanante_apellido1: String(form.acompanante_apellido1 || '').trim(),
          acompanante_apellido2: String(form.acompanante_apellido2 || '').trim(),
          acompanante_documento: String(form.acompanante_documento || '').trim(),
          additional_notes: form.additional_notes || '',
          custom_body: advanced ? (form.custom_body || '') : '',
        };
        if (sinDocumento) delete payload.acompanante_documento;
      } else {
        payload = { ...form };
        delete payload.cobro_id;
        // Los datos de un acompañante solo van en el de acompañante.
        Object.keys(payload).filter((k) => k.startsWith('acompanante_')).forEach((k) => { delete payload[k]; });
        // Vacío se manda como vacío al editar: así se puede borrar lo que había
        // (el servidor no toca lo que llega como null). Al crear, null.
        payload.rest_days = payload.rest_days ? parseInt(payload.rest_days, 10) : (isEdit ? 0 : null);
        ['time_from', 'time_to', 'procedure', 'companion_name', 'additional_notes', 'custom_body'].forEach((k) => {
          if (!isEdit && payload[k] === '') payload[k] = null;
        });
        if (!advanced) payload.custom_body = isEdit ? '' : null;
      }
      if (!payload.marca_clave) delete payload.marca_clave;
      if (isEdit) {
        await api.put(`/api/justificantes/${initial.id}`, payload);
        toast.success('Justificante actualizado');
      } else {
        await api.post('/api/justificantes', payload);
        toast.success('Justificante creado');
      }
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error';
      toast.error(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar justificante' : 'Nuevo justificante'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>{isAcomp ? 'Paciente al que acompaña *' : 'Paciente *'}</Label>
            {pacienteFijo ? (
              <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/40 text-sm" data-testid="just-patient-fijo">
                {pacienteFijo}
              </div>
            ) : (
              <PatientCombobox
                value={form.patient_id}
                onChange={(v) => { setCobroConfirmado(false); setForm((f) => ({ ...f, patient_id: v, cobro_id: '' })); }}
                patients={patients}
                placeholder="Seleccionar paciente…"
                dataTestid="just-patient"
              />
            )}
          </div>

          <div>
            <Label>Tipo *</Label>
            <Select value={form.type} onValueChange={(v) => setF('type', v)} disabled={isEdit || !!preset}>
              <SelectTrigger data-testid="just-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="consulta">Justificante de consulta</SelectItem>
                <SelectItem value="quirofano">Justificante de quirófano (intervención)</SelectItem>
                {(puedePago || isPago) && <SelectItem value="pago">Justificante de pago</SelectItem>}
                <SelectItem value="acompanante">Justificante de acompañante</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* El formato (el centro): en todos, antes de generar el PDF. */}
          {centros && centros.length > 0 && (
            <div>
              <Label>Formato *</Label>
              <Select value={form.marca_clave || ''} onValueChange={elegirCentro}>
                <SelectTrigger data-testid="just-centro">
                  <SelectValue placeholder={isEdit ? 'Sin elegir: sale con los datos de Configuración' : 'Elegir formato…'} />
                </SelectTrigger>
                <SelectContent>
                  {centros.map((c) => <SelectItem key={c.clave} value={c.clave}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              {PISTA_DEL_CENTRO[motivo] && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="just-centro-pista">{PISTA_DEL_CENTRO[motivo]}</p>
              )}
            </div>
          )}
          {centros && centros.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="just-centro-sin">
              Sale con los datos de Configuración (no hay centros en Configuración → Marcas).
            </p>
          )}

          {isPago ? (
            <div className="space-y-2">
              <Label>Cobro *</Label>
              {isEdit ? (
                <div className="text-sm" data-testid="just-cobro-fijo">
                  {fechaCorta(initial?.event_date)} · {initial?.importe != null ? `${importeES(initial.importe)} €` : '—'}
                  {initial?.metodo_pago ? ` · ${initial.metodo_pago}` : ''}
                </div>
              ) : !pacienteId ? (
                <p className="text-sm text-muted-foreground">Elige primero el paciente.</p>
              ) : errorCobros ? (
                <p className="text-sm text-destructive" data-testid="just-cobros-error">{errorCobros}</p>
              ) : cobros === null ? (
                <p className="text-sm text-muted-foreground">Cargando cobros…</p>
              ) : cobros.length === 0 ? (
                <p className="text-sm text-destructive" data-testid="just-cobros-vacio">
                  Este paciente no tiene ningún cobro registrado: hay que registrar el cobro en Cobros antes de generar el justificante de pago (las devoluciones no cuentan).
                </p>
              ) : (
                <Select value={cobroElegido ? form.cobro_id : ''} onValueChange={elegirCobro}>
                  <SelectTrigger data-testid="just-cobro"><SelectValue placeholder="Elegir cobro…" /></SelectTrigger>
                  <SelectContent>
                    {cobros.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {`${fechaCorta(c.fecha)} · ${c.importe_texto} € · ${c.metodo || '—'}${c.concepto ? ` · ${c.concepto}` : ''}${c.aviso ? ' · revisar' : ''}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {cobroQueNoEsta && (
                <p className="text-sm text-destructive" data-testid="just-cobro-no-esta">
                  Ese cobro no está entre los de este paciente de los que sale justificante (una devolución no es un pago). Elige otro.
                </p>
              )}
              {hayQueConfirmar && (
                <div className="text-sm border border-amber-200 bg-amber-50 text-amber-900 p-2 rounded space-y-1" data-testid="just-cobro-iguales">
                  <p className="font-medium">
                    Hay {cobrosIguales.length} cobros con el mismo importe. ¿Cuál corresponde a este justificante?
                  </p>
                  {cobrosIguales.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="cobro-igual"
                        value={c.id}
                        checked={cobroConfirmado && form.cobro_id === c.id}
                        onChange={() => confirmarCobro(c.id)}
                        data-testid={`just-cobro-igual-${c.id}`}
                      />
                      <span>{`${fechaCorta(c.fecha)} · ${c.importe_texto} € · ${c.metodo || '—'}${c.concepto ? ` · ${c.concepto}` : ''}`}</span>
                    </label>
                  ))}
                </div>
              )}
              <div>
                <Label>Fecha del pago</Label>
                <Input value={fechaDelPago} readOnly tabIndex={-1} className="bg-muted/40" data-testid="just-fecha-pago" />
                <p className="text-xs text-muted-foreground mt-1" data-testid="just-fecha-pago-aviso">{AVISO_FECHA_DEL_PAGO}</p>
              </div>
              {cobroElegido?.aviso && (
                <div className="flex items-start gap-1.5 text-xs text-amber-900 bg-amber-50 border border-amber-200 p-2 rounded" data-testid="just-pago-aviso">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
                  <span>{cobroElegido.aviso}</span>
                </div>
              )}
              {textoPago && !advanced && (
                <div className="text-sm bg-[rgba(46,110,94,0.06)] border border-border p-2 rounded" data-testid="just-pago-texto">
                  {textoPago}
                </div>
              )}
            </div>
          ) : isAcomp ? (
            <div className="space-y-3" data-testid="just-acompanante">
              <p className="text-sm font-medium text-[#13231E]">Acompañante</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Nombre *</Label>
                  <Input value={form.acompanante_nombre || ''} maxLength={100} autoComplete="off"
                    onChange={(e) => setF('acompanante_nombre', e.target.value)} data-testid="just-acomp-nombre" />
                </div>
                <div>
                  <Label>Primer apellido *</Label>
                  <Input value={form.acompanante_apellido1 || ''} maxLength={100} autoComplete="off"
                    onChange={(e) => setF('acompanante_apellido1', e.target.value)} data-testid="just-acomp-apellido1" />
                </div>
                <div>
                  <Label>Segundo apellido</Label>
                  <Input value={form.acompanante_apellido2 || ''} maxLength={100} autoComplete="off"
                    onChange={(e) => setF('acompanante_apellido2', e.target.value)} data-testid="just-acomp-apellido2" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  {/* Como en la LOPD: DNI, NIE, pasaporte o uno de otro país. */}
                  <Label>DNI / NIE / Pasaporte *</Label>
                  <Input
                    className="uppercase"
                    value={form.acompanante_documento || ''}
                    maxLength={20}
                    autoComplete="off"
                    disabled={isEdit && docAcomp === 'cargando'}
                    placeholder={isEdit && docAcomp === 'cargando' ? 'Cargando…' : ''}
                    onChange={(e) => setF('acompanante_documento', e.target.value.toUpperCase())}
                    data-testid="just-acomp-documento"
                  />
                  {isEdit && docAcomp === 'error' && (
                    <p className="text-xs text-destructive mt-1" data-testid="just-acomp-documento-error">
                      No se ha podido cargar el documento. Si no escribes otro, se queda el que tenía.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Fecha de la visita *</Label>
                  <Input type="date" required value={form.event_date || ''} onChange={(e) => setF('event_date', e.target.value)} data-testid="just-event-date" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="just-acomp-sin-ficha">
                El acompañante no necesita ser paciente: no se le crea ficha. Sus datos se guardan solo en este justificante, con el paciente al que acompaña.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label>Fecha del evento *</Label>
                  <Input type="date" required value={form.event_date || ''} onChange={(e) => setF('event_date', e.target.value)} data-testid="just-event-date" />
                </div>
                {!isQX && (
                  <>
                    <div>
                      <Label>Hora desde</Label>
                      <Input type="time" value={form.time_from || ''} onChange={(e) => setF('time_from', e.target.value)} />
                    </div>
                    <div>
                      <Label>Hora hasta</Label>
                      <Input type="time" value={form.time_to || ''} onChange={(e) => setF('time_to', e.target.value)} />
                    </div>
                  </>
                )}
                {isQX && (
                  <div className="sm:col-span-2">
                    <Label>Procedimiento</Label>
                    <Input value={form.procedure || ''} onChange={(e) => setF('procedure', e.target.value)} placeholder="Blefaroplastia superior bilateral" data-testid="just-procedure" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Días de reposo recomendados</Label>
                  <Input type="number" min={0} value={form.rest_days || ''} onChange={(e) => setF('rest_days', e.target.value)} placeholder={isQX ? '10' : '1'} />
                </div>
                <div>
                  <Label>Acompañante (opcional)</Label>
                  <Input value={form.companion_name || ''} onChange={(e) => setF('companion_name', e.target.value)} placeholder="Nombre del acompañante" />
                </div>
              </div>
            </>
          )}

          <div>
            <Label>Notas adicionales (opcional)</Label>
            <Textarea rows={2} value={form.additional_notes || ''} onChange={(e) => setF('additional_notes', e.target.value)} placeholder="Texto que se añade al final del cuerpo del justificante" />
          </div>

          <div className="border-t border-border pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer text-[#13231E]">
              <input type="checkbox" checked={advanced} onChange={(e) => cambiarTexto(e.target.checked)} data-testid="just-advanced" />
              {isPago ? 'Editar el texto del justificante antes de imprimir' : 'Reemplazar todo el cuerpo del justificante con texto personalizado'}
            </label>
            {advanced && (
              <Textarea
                rows={6}
                value={form.custom_body || ''}
                onChange={(e) => setF('custom_body', e.target.value)}
                placeholder="Escribe aquí el texto completo del justificante. Sustituye al texto generado automáticamente."
                className="mt-2"
                data-testid="just-custom-body"
              />
            )}
          </div>

          <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-[rgba(46,110,94,0.08)] p-2 rounded">
            <Info className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
            <span>El justificante incluye automáticamente: la cabecera del centro elegido (logo, nombre, dirección y teléfono), {isAcomp ? 'los datos del acompañante y del paciente al que acompaña' : 'los datos del paciente'} y la firma del centro. Puedes editar el contenido tantas veces como necesites antes de imprimir.</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" disabled={sinCobro} data-testid="just-save">
              {isEdit ? <><Edit2 className="w-4 h-4 mr-2" />Guardar</> : <><Plus className="w-4 h-4 mr-2" />Crear</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function detalle(j) {
  if (j.type === 'quirofano') return (j.procedure || '—') + (j.rest_days ? ` · ${j.rest_days}d reposo` : '');
  if (j.type === 'pago') {
    const importe = j.importe != null ? `${importeES(j.importe)} €` : '—';
    return `${importe}${j.metodo_pago ? ` · ${j.metodo_pago}` : ''}`;
  }
  // Quién acompañó (su documento no viene en el listado).
  if (j.type === 'acompanante') return `Acompañante: ${nombreAcompanante(j) || '—'}`;
  return j.time_from && j.time_to ? `${j.time_from} – ${j.time_to}` : '—';
}

export default function JustificationsPage() {
  const { user } = useAuth();
  // El de pago es dinero: gestión, como los cobros (el servidor tampoco se lo
  // da a nadie más).
  const puedePago = isGestion(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultType, setDefaultType] = useState('consulta');
  const [preset, setPreset] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get('/api/justificantes'),
        api.get('/api/patients'),
      ]);
      setItems(r1.data);
      setPatients(r2.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);
  // Los nombres de los centros, para la columna «Centro».
  useEffect(() => {
    api.get('/api/justificantes/centros')
      .then((r) => setCentros(Array.isArray(r?.data?.centros) ? r.data.centros : []))
      .catch(() => setCentros([]));
  }, []);

  // Venir desde un cobro (botón «Justificante» de Cobros): el de pago de ese
  // cobro, con su paciente, ya abierto.
  useEffect(() => {
    const cobro = searchParams.get('pago');
    const paciente = searchParams.get('paciente');
    if (!cobro || !paciente) return;
    setEditing(null);
    setDefaultType('pago');
    setPreset({ patient_id: paciente, cobro_id: cobro, type: 'pago' });
    setOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const nombreDelCentro = useMemo(() => Object.fromEntries(centros.map((c) => [c.clave, c.nombre])), [centros]);

  const visible = useMemo(() => {
    let arr = items;
    if (tab !== 'all') arr = arr.filter((it) => it.type === tab);
    if (q) {
      const qq = normalizaTexto(q);
      arr = arr.filter((it) => normalizaTexto(it.patient_name).includes(qq) || normalizaTexto(it.procedure).includes(qq));
    }
    return arr;
  }, [items, q, tab]);

  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar justificante?')) return;
    await api.delete(`/api/justificantes/${id}`);
    toast.success('Eliminado');
    fetchAll();
  };

  const openPdf = (j) => {
    const t = TIPOS[j.type] || TIPOS.consulta;
    setPreview({
      open: true,
      url: `${BACKEND_URL}/api/justificantes/${j.id}/pdf`,
      filename: `justificante_${j.type in TIPOS ? j.type : 'consulta'}_${j.patient_name}.pdf`,
      title: `${t.titulo} · ${j.patient_name}`,
    });
  };

  const openNew = (type) => {
    setEditing(null);
    setPreset(null);
    setDefaultType(type);
    setOpen(true);
  };

  const cuantos = (t) => items.filter((i) => i.type === t).length;

  return (
    <div>
      <PageHeader
        title="Justificantes"
        subtitle="Documentos de asistencia a consulta, de intervención quirúrgica, de pago y de acompañante, editables antes de imprimir."
        actions={
          <>
            {/* Iconos de lucide en vez de emojis, que en los equipos de la
                clínica salían como cuadrados (5/10/2026). */}
            <Button variant="outline" onClick={() => openNew('consulta')} data-testid="just-new-consulta">
              <Stethoscope className="w-4 h-4 mr-2" />Consulta
            </Button>
            {puedePago && (
              <Button variant="outline" onClick={() => openNew('pago')} data-testid="just-new-pago">
                <Receipt className="w-4 h-4 mr-2" />Pago
              </Button>
            )}
            <Button variant="outline" onClick={() => openNew('acompanante')} data-testid="just-new-acompanante">
              <Users className="w-4 h-4 mr-2" />Acompañante
            </Button>
            <Button onClick={() => openNew('quirofano')} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="just-new-quirofano">
              <Scissors className="w-4 h-4 mr-2" />Quirófano
            </Button>
          </>
        }
      />

      <div className="cjo-card p-4 mb-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar paciente o procedimiento…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="just-search" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Cuatro pestañas (con la de pago, 5/10/2026): en el móvil pasan a
            una segunda línea en vez de desplazar la página de lado. */}
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="just-tab-all">Todos ({items.length})</TabsTrigger>
          <TabsTrigger value="consulta" data-testid="just-tab-consulta">Consulta ({cuantos('consulta')})</TabsTrigger>
          <TabsTrigger value="quirofano" data-testid="just-tab-quirofano">Quirófano ({cuantos('quirofano')})</TabsTrigger>
          {puedePago && (
            <TabsTrigger value="pago" data-testid="just-tab-pago">Pago ({cuantos('pago')})</TabsTrigger>
          )}
          <TabsTrigger value="acompanante" data-testid="just-tab-acompanante">Acompañante ({cuantos('acompanante')})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="cjo-card overflow-hidden">
            {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
              : visible.length === 0
              ? <EmptyState icon={ClipboardCheck} title="Sin justificantes" description="Crea el primero para tu paciente." />
              : <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                  <tr>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Tipo</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Fecha evento</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Detalle</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Centro</th>
                    <th className="py-3 px-4 font-semibold text-[#13231E]">Emitido</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((j) => {
                    const t = TIPOS[j.type] || TIPOS.consulta;
                    const Icono = t.icono;
                    return (
                      <tr key={j.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="just-row">
                        <td className="py-3 px-4">
                          <Badge variant="secondary" className={j.type === 'consulta' ? 'bg-[rgba(46,110,94,0.15)] text-[#13231E] border-[rgba(46,110,94,0.30)]' : 'bg-[rgba(46,110,94,0.10)] text-[#13231E] border-[rgba(46,110,94,0.25)]'}>
                            <Icono className="w-3 h-3 mr-1" aria-hidden="true" />{t.etiqueta}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{j.patient_name}</td>
                        <td className="py-3 px-4 text-xs">{formatDate(j.event_date)}</td>
                        <td className="py-3 px-4 text-xs text-muted-foreground">{detalle(j)}</td>
                        <td className="py-3 px-4 text-xs" data-testid="just-row-centro">
                          {j.marca_clave ? (nombreDelCentro[j.marca_clave] || j.marca_clave) : <span className="text-muted-foreground">Configuración</span>}
                        </td>
                        <td className="py-3 px-4 text-xs">{formatDate(j.issue_date || j.created_at)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-1 items-center justify-end">
                            <Button size="sm" variant="outline" onClick={() => openPdf(j)} title="Ver / Imprimir" data-testid="just-pdf-button">
                              <FileText className="w-4 h-4 mr-1" /> PDF
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(j); setPreset(null); setOpen(true); }}>Editar</Button>
                            <Button size="sm" variant="ghost" onClick={() => onDelete(j.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            }
          </div>
        </TabsContent>
      </Tabs>

      <JustificationDialog
        open={open}
        onOpenChange={setOpen}
        initial={editing}
        patients={patients}
        defaultType={defaultType}
        preset={preset}
        puedePago={puedePago}
        onSaved={fetchAll}
      />
      <PdfPreviewDialog
        open={preview.open}
        onOpenChange={(v) => setPreview((s) => ({ ...s, open: v }))}
        url={preview.url}
        filename={preview.filename}
        title={preview.title}
      />
    </div>
  );
}
